import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';


const AllEmployeesGoalsReview = () => {
  const baseUrl = API_BASE_URL;
  const [goals, setGoals] = useState([]);
  const [groupedGoals, setGroupedGoals] = useState([]);
  const [flagging, setFlagging] = useState({});
  const [flagFeedback, setFlagFeedback] = useState({});
  const [expandedActivity, setExpandedActivity] = useState({});
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const employeeId = location.state?.employeeId || id;
  const [employeeName, setEmployeeName] = useState('');
  const navigate = useNavigate();
  
  // Year filter state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filteredGoals, setFilteredGoals] = useState([]);

  // Function to filter goals by year
  const filterGoalsByYear = (goalsList, year) => {
    return goalsList.filter(goal => {
      if (goal.year !== undefined && goal.year !== null) {
        return goal.year === year;
      }
      
      const goalDate = goal.created_at || goal.updated_at || goal.start_date || goal.end_date || goal.date_created;
      if (!goalDate) {
        return true;
      }
      
      const goalYear = new Date(goalDate).getFullYear();
      return goalYear === year;
    });
  };

  // Add global CSS animation for blinking unreviewed goals
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes blinkUnreviewed {
        0% { background-color: #fef3c7 !important; }
        50% { background-color: #fbbf24 !important; }
        100% { background-color: #fef3c7 !important; }
      }
      .unreviewed-row {
        animation: blinkUnreviewed 1.5s infinite !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [hodScore, setHodScore] = useState('');
  const [modalError, setModalError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewGoal, setViewGoal] = useState(null);

  // Flag modal state
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagGoalId, setFlagGoalId] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [flagModalError, setFlagModalError] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);

  useEffect(() => {
    const fetchGoals = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/employees/${employeeId}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const completedGoals = (Array.isArray(response?.data?.employee?.goals) ? response?.data?.employee?.goals : []).filter(goal => goal.status === 'complete' || goal.status === 'completed');
        setGoals(completedGoals);
        setEmployeeName(response?.data?.employee?.name || response?.data?.employee?.full_name || '');

        const yearFilteredGoals = filterGoalsByYear(completedGoals, selectedYear);
        setFilteredGoals(yearFilteredGoals);

        const kraMap = {};
        yearFilteredGoals.forEach(goal => {
          const kraName = goal.key_result_area?.name || goal.kra_name || goal.key_result_area || 'Uncategorized';
          const kraPercentage = goal.key_result_area?.percentage || 0;
          if (!kraMap[kraName]) kraMap[kraName] = { goals: [], percentage: kraPercentage };
          kraMap[kraName].goals.push(goal);
        });
        const grouped = Object.entries(kraMap).map(([kra, value]) => ({
          kra,
          goals: value.goals,
          percentage: value.percentage
        }));
        setGroupedGoals(grouped);
      } catch (err) {
        setError('Failed to fetch goals.');
      } finally {
        setLoading(false);
      }
    };
    fetchGoals();
  }, [baseUrl, employeeId, selectedYear]);

  useEffect(() => {
    if (goals.length > 0) {
      const yearFilteredGoals = filterGoalsByYear(goals, selectedYear);
      setFilteredGoals(yearFilteredGoals);
      
      const kraMap = {};
      yearFilteredGoals.forEach(goal => {
        const kraName = goal.key_result_area?.name || goal.kra_name || goal.key_result_area || 'Uncategorized';
        const kraPercentage = goal.key_result_area?.percentage || 0;
        if (!kraMap[kraName]) kraMap[kraName] = { goals: [], percentage: kraPercentage };
        kraMap[kraName].goals.push(goal);
      });
      const grouped = Object.entries(kraMap).map(([kra, value]) => ({
        kra,
        goals: value.goals,
        percentage: value.percentage
      }));
      setGroupedGoals(grouped);
    }
  }, [selectedYear, goals]);

  const handleReviewClick = (goal) => {
    setSelectedGoal(goal);
    setHodScore('');
    setModalError('');
    setModalOpen(true);
  };

  const handleViewClick = (goal) => {
    setViewGoal(goal);
    setViewModalOpen(true);
  };

  const normalizeText = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  const renderTruncatedActivity = (activityValue, rowKey, maxChars = 50) => {
    const fullText = normalizeText(activityValue).trim();
    const key = `activity-${rowKey}`;
    const isExpanded = !!expandedActivity[key];

    if (!fullText) return '[No Activity]';
    if (fullText.length <= maxChars) return fullText;

    const shownText = isExpanded ? fullText : `${fullText.slice(0, maxChars)}...`;

    return (
      <span>
        <span style={{ whiteSpace: 'pre-wrap' }}>{shownText}</span>
        <button
          type="button"
          onClick={() => setExpandedActivity(prev => ({ ...prev, [key]: !prev[key] }))}
          className="btn btn-link btn-sm p-0 ms-2 text-decoration-none fw-semibold"
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </button>
      </span>
    );
  };

  const generateReportData = () => {
    const kraMap = {};
    filteredGoals.forEach(g => {
      const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized';
      if (!kraMap[kraName]) kraMap[kraName] = [];
      kraMap[kraName].push(g);
    });

    const totalHod = filteredGoals.reduce((sum, g) => sum + (Number(g.hod_score) || 0), 0);
    const totalMetric = filteredGoals.reduce((sum, g) => sum + (Number(g.metric) || 0), 0);
    const percentage = totalMetric > 0 ? ((totalHod / totalMetric) * 100).toFixed(2) : '0.00';

    return {
      employeeName,
      year: selectedYear,
      kras: Object.entries(kraMap),
      totalHod,
      totalMetric,
      percentage,
      goals: filteredGoals
    };
  };

  const generatePDF = async () => {
    try {
      const reportData = generateReportData();
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('p', 'mm', 'a4');

      const loadLogoDataUrl = async () => {
        const response = await fetch('/celebration logo.png');
        const blob = await response.blob();
        return await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      };

      let logoDataUrl = null;
      try {
        logoDataUrl = await loadLogoDataUrl();
      } catch (err) {
        console.warn('Logo could not be loaded for PDF watermark:', err);
      }

      const drawWatermark = (pageNumber = 1) => {
        if (!logoDataUrl) return;
        doc.setPage(pageNumber);
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const wmWidth = 120;
        const wmHeight = 90;
        const x = (pageWidth - wmWidth) / 2;
        const y = (pageHeight - wmHeight) / 2;
        doc.addImage(logoDataUrl, 'PNG', x, y, wmWidth, wmHeight);
      };
      
      const primaryColor = [59, 130, 246];
      const darkGray = [55, 65, 81];
      const lightGray = [156, 163, 175];
      
      const wrapText = (text, maxWidth) => {
        const words = text.split(' ');
        const lines = [];
        let currentLine = '';
        
        for (let word of words) {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const testWidth = doc.getTextWidth(testLine);
          
          if (testWidth > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine) lines.push(currentLine);
        return lines;
      };

      const cssColorToRgb = (color) => {
        if (!color) return null;
        const c = String(color).trim();
        const hexMatch = c.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
        if (hexMatch) {
          let hex = hexMatch[1];
          if (hex.length === 3) hex = hex.split('').map(ch => ch + ch).join('');
          const r = parseInt(hex.slice(0, 2), 16);
          const g = parseInt(hex.slice(2, 4), 16);
          const b = parseInt(hex.slice(4, 6), 16);
          return [r, g, b];
        }
        const rgbMatch = c.match(/^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i);
        if (rgbMatch) {
          const r = Math.min(255, Math.max(0, parseInt(rgbMatch[1], 10)));
          const g = Math.min(255, Math.max(0, parseInt(rgbMatch[2], 10)));
          const b = Math.min(255, Math.max(0, parseInt(rgbMatch[3], 10)));
          return [r, g, b];
        }
        return null;
      };
      
      const drawTable = (headers, rows, startY, maxWidth = 180) => {
        const colWidths = [70, 45, 18, 15, 32];
        const rowHeight = 8;
        const headerHeight = 10;
        
        doc.setFillColor(...primaryColor);
        doc.rect(15, startY, maxWidth, headerHeight, 'F');
        
        let x = 15;
        headers.forEach((header, i) => {
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.text(header, x + 2, startY + 6);
          x += colWidths[i];
        });
        
        let currentY = startY + headerHeight;
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        
        rows.forEach((row, rowIndex) => {
          if (rowIndex % 2 === 0) {
            doc.setFillColor(249, 250, 251);
            doc.rect(15, currentY, maxWidth, rowHeight, 'F');
          }
          
          x = 15;
          let maxLines = 1;
          row.forEach((cell, colIndex) => {
            if (colIndex === 3 && cell && typeof cell === 'object') {
              const ragLabel = cell.label ? String(cell.label) : '';
              const rgb = cssColorToRgb(cell.color) || [229, 231, 235];

              doc.setFillColor(...rgb);
              doc.roundedRect(x + 3, currentY + 2, colWidths[colIndex] - 6, rowHeight - 4, 2, 2, 'F');

              if (ragLabel) {
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(8);
                doc.text(ragLabel, x + (colWidths[colIndex] / 2), currentY + 5.8, { align: 'center' });
                doc.setTextColor(0, 0, 0);
                doc.setFontSize(9);
              }
            } else {
              const cellText = String(cell ?? '');
              const wrappedText = wrapText(cellText, colWidths[colIndex] - 4);
              maxLines = Math.max(maxLines, wrappedText.length);

              wrappedText.forEach((line, lineIndex) => {
                doc.text(line, x + 2, currentY + 5 + (lineIndex * 3.5));
              });
            }
            
            x += colWidths[colIndex];
          });
          
          currentY += Math.max(rowHeight, maxLines * 3.5 + 2);
        });
        
        return currentY;
      };
      
      let yPosition = 20;
      drawWatermark(1);
      
      doc.setTextColor(...primaryColor);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Performance Report', 105, yPosition, { align: 'center' });
      
      yPosition += 15;
      doc.setTextColor(...darkGray);
      doc.setFontSize(16);
      doc.setFont(undefined, 'normal');
      doc.text(`for ${employeeName} - Year: ${selectedYear}`, 105, yPosition, { align: 'center' });
      
      yPosition += 8;
      
      doc.setFillColor(248, 250, 252);
      doc.rect(15, yPosition, 180, 25, 'F');
      doc.setDrawColor(...primaryColor);
      doc.rect(15, yPosition, 180, 35);
      
      yPosition += 8;
      doc.setTextColor(...darkGray);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Performance Summary', 20, yPosition);
      
      yPosition += 8;
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.text(`Total Final Score: ${reportData.totalHod} / ${reportData.totalMetric}`, 20, yPosition);
      
      yPosition += 5;
      doc.text(`Performance Percentage: ${reportData.percentage}%`, 20, yPosition);
      
      yPosition += 5;
      doc.text(`Number of Goals: ${reportData.goals.length}`, 20, yPosition);
      
      yPosition += 20;
      
      reportData.kras.forEach(([kraName, kraGoals]) => {
        const kraTotal = kraGoals.reduce((s, g) => s + (Number(g.hod_score) || 0), 0);
        const kraMetric = kraGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0);

        if (yPosition > 250) {
          doc.addPage();
          drawWatermark(doc.internal.getNumberOfPages());
          yPosition = 20;
        }
        
        doc.setFillColor(...primaryColor);
        doc.rect(15, yPosition, 180, 12, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`KRA: ${kraName} (${kraTotal}/${kraMetric})`, 20, yPosition + 8);
        
        yPosition += 15;
        
        const headers = ['Activity/KPI', 'Standard', 'Metric', 'RAG', 'Final Score'];
        const rows = kraGoals.map(goal => [
          goal.activity || 'N/A',
          goal.performance_standard || 'N/A',
          goal.metric || 'N/A',
          { label: goal.rag_color || '', color: goal.progress_indicator },
          goal.agreed_score !== 0 ? goal.agreed_score : 'Not Reviewed',
        ]);
        
        rows.push(['KRA Total', '', `${kraMetric}`, '', `${kraTotal}/${kraMetric}`]);
        
        yPosition = drawTable(headers, rows, yPosition);
        yPosition += 15;
      });
      
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setTextColor(...lightGray);
        doc.setFontSize(8);
        doc.text('Generated by Performance Management System', 15, 290);
        doc.text(`Page ${i} of ${pageCount}`, 175, 290, { align: 'right' });
      }
      
      const fileName = `Performance Report for ${employeeName} for ${selectedYear}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal) return;
    const maxScore = selectedGoal.metric;
    const parsedScore = Number(hodScore);
    if (isNaN(parsedScore) || parsedScore < 0) {
      setModalError('Please enter a valid score.');
      return;
    }
    if (parsedScore > maxScore) {
      setModalError(`HOD score cannot be greater than the goal metric (${maxScore}).`);
      return;
    }
    setSubmitting(true);
    setModalError('');
    try {
      const token = Cookies.get('token');
      await axios.post(
        `${baseUrl}/api/goals/${selectedGoal.id}/score/`,
        { hod_score: parsedScore },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setGoals(goals => goals.map(g => g.id === selectedGoal.id ? { ...g, hod_score: parsedScore } : g));
      setFilteredGoals(filteredGoals => filteredGoals.map(g => g.id === selectedGoal.id ? { ...g, hod_score: parsedScore } : g));
      setGroupedGoals(groupedGoals => groupedGoals.map(group => ({
        ...group,
        goals: group.goals.map(g => g.id === selectedGoal.id ? { ...g, hod_score: parsedScore } : g)
      })));
      
      setModalOpen(false);
      setSelectedGoal(null);
      setHodScore('');
    } catch (err) {
      setModalError('Failed to submit HOD score.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFlagUnfair = (goalId) => {
    setFlagGoalId(goalId);
    setFlagReason('');
    setFlagModalError('');
    setFlagModalOpen(true);
  };

  const handleFlagSubmit = async (e) => {
    e.preventDefault();
    if (!flagGoalId) return;
    if (!flagReason || flagReason.trim().length < 3) {
      setFlagModalError('Please provide a brief reason (min 3 characters).');
      return;
    }
    setFlagSubmitting(true);
    setFlagging(f => ({ ...f, [flagGoalId]: true }));
    setFlagFeedback(f => ({ ...f, [flagGoalId]: '' }));
    try {
      const token = Cookies.get('token');
      await axios.post(`${baseUrl}/api/goals/${flagGoalId}/flag/`, { flagging_reason: flagReason.trim() }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setFlagFeedback(f => ({ ...f, [flagGoalId]: 'success' }));
      setGoals(goals => goals.map(g => g.id === flagGoalId ? { ...g, flagged: true } : g));
      setFilteredGoals(filteredGoals => filteredGoals.map(g => g.id === flagGoalId ? { ...g, flagged: true } : g));
      setGroupedGoals(groupedGoals => groupedGoals.map(group => ({
        ...group,
        goals: group.goals.map(g => g.id === flagGoalId ? { ...g, flagged: true } : g)
      })));
      
      setFlagModalOpen(false);
      setFlagGoalId(null);
      setFlagReason('');
    } catch (err) {
      setFlagFeedback(f => ({ ...f, [flagGoalId]: 'error' }));
      setFlagModalError('Failed to flag this goal. Please try again.');
    } finally {
      setFlagSubmitting(false);
      setFlagging(f => ({ ...f, [flagGoalId]: false }));
    }
  };

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <Topbar />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content pb-0">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div className="d-flex align-items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
              >
                <i className="ti ti-arrow-left" /> Back
              </button>
              <div>
                <h4 className="fw-bold mb-1">Employee Goals Review</h4>
                <p className="text-muted mb-0 fs-13">Review completed employee goals and assign HOD scores</p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0 fs-14 text-muted">Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                >
                  {Array.from({ length: 2 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>

              {filteredGoals.length > 0 && (
                <button
                  type="button"
                  onClick={generatePDF}
                  className="btn btn-sm btn-danger d-flex align-items-center gap-1"
                  title="Download PDF Report"
                >
                  <i className="ti ti-file-text fs-16" />
                  <i className="ti ti-download fs-14" />
                  <span>Download PDF</span>
                </button>
              )}

              {employeeName && (
                <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2 fs-14 rounded-pill">
                  <i className="ti ti-user me-1" />
                  {employeeName}
                </span>
              )}
            </div>
          </div>

          {/* Main Body */}
          {loading ? (
            <div className="card shadow-sm p-5 text-center">
              <div className="spinner-border text-primary mx-auto mb-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mb-0">Loading goals...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger shadow-sm" role="alert">
              <i className="ti ti-alert-circle me-2" />
              {error}
            </div>
          ) : groupedGoals.length === 0 ? (
            <div className="card shadow-sm p-5 text-center text-muted">
              <i className="ti ti-target-off fs-36 mb-2 text-secondary opacity-50" />
              No completed goals found for {selectedYear}.
            </div>
          ) : (
            <>
              {groupedGoals.map((group, idx) => (
                <div key={group.kra || idx} className="card shadow-sm mb-4">
                  <div className="card-header bg-light border-bottom py-3">
                    <h6 className="card-title mb-0 fw-bold text-dark">
                      KRA: <span className="text-primary">{group.kra}</span>
                      <span className="badge bg-secondary ms-2">Weight: {group.percentage}%</span>
                    </h6>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Activity / Key Performance Indicator</th>
                          <th>Performance Standard</th>
                          <th style={{ width: '120px' }}>RAG</th>
                          <th>KPI Metric</th>
                          <th>Self Score</th>
                          <th>HOD Score</th>
                          <th>Agreed Score</th>
                          <th className="text-center" style={{ width: '120px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.goals.map((goal, gidx) => {
                          const isUnreviewed = (goal.hod_score === 0 || goal.hod_score === undefined);
                          return (
                            <tr
                              key={goal.id || gidx}
                              className={isUnreviewed ? 'unreviewed-row' : ''}
                            >
                              <td>{renderTruncatedActivity(goal.activity, goal.id || gidx)}</td>
                              <td>{goal.performance_standard !== undefined ? goal.performance_standard : '-'}</td>
                              <td>
                                <span
                                  className="badge w-100 text-white"
                                  style={{ backgroundColor: goal.progress_indicator || '#6c757d' }}
                                >
                                  {goal.rag_color || 'N/A'}
                                </span>
                              </td>
                              <td>{goal.metric !== undefined ? goal.metric : '%'}</td>
                              <td>{goal.score !== undefined ? goal.score : '-'}</td>
                              <td>{goal.hod_score !== undefined ? goal.hod_score : '-'}</td>
                              <td>{goal.agreed_score !== undefined ? goal.agreed_score : '-'}</td>
                              <td className="text-center">
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                  {goal.flagged ? (
                                    <span className="badge bg-danger-subtle text-danger border border-danger-subtle d-inline-flex align-items-center gap-1">
                                      <i className="ti ti-flag-filled" />
                                      Flagged
                                    </span>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-icon btn-light text-warning"
                                        title="Flag Unfair HOD Score"
                                        disabled={flagging[goal.id]}
                                        onClick={() => handleFlagUnfair(goal.id)}
                                      >
                                        <i className="ti ti-flag" />
                                      </button>
                                      {flagFeedback[goal.id] === 'success' && (
                                        <span className="badge bg-success-subtle text-success">Flagged</span>
                                      )}
                                      {flagFeedback[goal.id] === 'error' && (
                                        <span className="badge bg-danger-subtle text-danger">Error</span>
                                      )}
                                    </>
                                  )}
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-icon btn-light text-primary"
                                    title="View Goal Details"
                                    onClick={() => handleViewClick(goal)}
                                  >
                                    <i className="ti ti-eye" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="bg-light fw-bold">
                          <td colSpan={3} className="text-end text-uppercase fs-12 text-muted">KRA Final Total</td>
                          <td colSpan={5} className="text-dark">
                            {group.goals.reduce((s, g) => s + (Number(g.hod_score) || 0), 0)} / {group.goals.reduce((s, g) => s + (Number(g.metric) || 0), 0)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {/* Final Total Summary Card */}
              {filteredGoals.length > 0 && (() => {
                const totalHod = filteredGoals.reduce((sum, g) => sum + (Number(g.hod_score) || 0), 0);
                const totalMetric = filteredGoals.reduce((sum, g) => sum + (Number(g.metric) || 0), 0);
                const pct = totalMetric > 0 ? ((totalHod / totalMetric) * 100).toFixed(2) : '0.00';
                return (
                  <div className="card shadow-sm mb-4 bg-primary-subtle border border-primary-subtle">
                    <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <span className="fw-bold text-dark fs-16">Final Performance Summary</span>
                      <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold text-dark fs-16">
                          Total Score: <span className="text-primary">{totalHod} / {totalMetric}</span>
                        </span>
                        <span className="badge bg-primary fs-14 px-3 py-2 rounded-pill">
                          {pct}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
          <p className="text-dark mb-0">
            2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
          </p>
        </div>
      </div>

      {/* Review Modal */}
      {modalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Review Goal</h5>
                <button type="button" className="btn-close" onClick={() => setModalOpen(false)} aria-label="Close" />
              </div>
              {selectedGoal && (
                <form onSubmit={handleModalSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label text-muted fs-13 mb-1">Activity</label>
                      <div className="fw-semibold text-dark">{selectedGoal.activity}</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label text-muted fs-13 mb-1">Goal Metric</label>
                      <div className="fw-semibold text-dark">{selectedGoal.metric}</div>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="hod-score-input" className="form-label fw-medium">HOD Score</label>
                      <input
                        id="hod-score-input"
                        type="number"
                        min="0"
                        max={selectedGoal.metric}
                        value={hodScore}
                        onChange={e => setHodScore(e.target.value)}
                        className="form-control"
                        required
                        disabled={submitting}
                      />
                    </div>
                    {modalError && (
                      <div className="alert alert-danger py-2 mb-0" role="alert">
                        {modalError}
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)} disabled={submitting}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-success" disabled={submitting}>
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                          Submitting...
                        </>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Flag Modal */}
      {flagModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Flag Unfair HOD Score</h5>
                <button type="button" className="btn-close" onClick={() => setFlagModalOpen(false)} aria-label="Close" />
              </div>
              <form onSubmit={handleFlagSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-medium">Please provide a brief reason:</label>
                    <textarea
                      value={flagReason}
                      onChange={e => setFlagReason(e.target.value)}
                      rows={4}
                      className="form-control"
                      placeholder="Describe why the HOD score is unfair"
                      disabled={flagSubmitting}
                    />
                  </div>
                  {flagModalError && (
                    <div className="alert alert-danger py-2 mb-0" role="alert">
                      {flagModalError}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setFlagModalOpen(false)} disabled={flagSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-warning text-dark fw-medium" disabled={flagSubmitting}>
                    {flagSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        Submitting...
                      </>
                    ) : (
                      'Submit'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {viewModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Goal Details</h5>
                <button type="button" className="btn-close" onClick={() => setViewModalOpen(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                {viewGoal && (
                  <>
                    {viewGoal.flagged && viewGoal.flagging_reason && (
                      <div className="alert alert-danger d-flex align-items-start gap-2 mb-3" role="alert">
                        <i className="ti ti-flag-filled fs-18 mt-1" />
                        <div>
                          <strong className="d-block">Flag Reason</strong>
                          <span style={{ whiteSpace: 'pre-wrap' }}>{viewGoal.flagging_reason}</span>
                        </div>
                      </div>
                    )}
                    <div className="table-responsive border rounded mb-3">
                      <table className="table table-striped mb-0">
                        <tbody>
                          {[
                            { label: 'Activity / KPI', value: viewGoal.activity },
                            { label: 'Performance Standard / Target', value: viewGoal.performance_standard },
                            { label: 'Metric', value: viewGoal.metric || viewGoal.score },
                            { label: 'Self Score', value: viewGoal.score },
                            { label: 'HOD Score', value: viewGoal.hod_score !== undefined ? viewGoal.hod_score : '-' },
                          ].map((row) => (
                            <tr key={row.label}>
                              <th style={{ width: '35%' }} className="text-muted fw-semibold fs-14">{row.label}</th>
                              <td className="fw-medium text-dark" style={{ whiteSpace: 'pre-wrap' }}>{row.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {viewGoal.goal_documents && (
                      <div className="mt-3">
                        <a
                          href={`${baseUrl}` + viewGoal.goal_documents.document}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-teal text-white btn-sm d-inline-flex align-items-center gap-1"
                        >
                          <i className="ti ti-file-download" /> View Document
                        </a>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setViewModalOpen(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllEmployeesGoalsReview;
